import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ExternalLink, X } from 'lucide-react'
import { ErrorState, LoadingState, errorMessage } from '@/components/common/States'
import { Composer } from '@/components/ticket/Composer'
import { Thread } from '@/components/ticket/Thread'
import { TicketRail } from '@/components/ticket/TicketRail'
import { AssigneeMenu, PriorityMenu, StatusMenu } from '@/components/ticket/TicketQuickFields'
import { ResolveDialog } from '@/components/ticket/ResolveDialog'
import { ReopenDialog } from '@/components/ticket/TicketActionDialogs'
import { useDownloadAttachment } from '@/hooks/useAttachments'
import { useMessages } from '@/hooks/useMessages'
import { useNow } from '@/hooks/useNow'
import { useTicket } from '@/hooks/useTickets'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { buildThread, replyTarget, viewerTimeZone } from '@/lib/threadView'

/**
 * A ticket as a slide-over next to the board (z8nrz7fy9a), like ClickUp's task
 * view. It reuses the ticket page's parts — details rail (status / assignee /
 * priority with If-Match), conversation and composer — so every edit behaves
 * and is audited exactly as on the full page.
 */
export function TicketPanel({ ticketKey, onClose }: { ticketKey: string | null; onClose: () => void }) {
  return (
    <DialogPrimitive.Root open={ticketKey !== null} onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-ink/20 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[1080px] flex-col border-l border-line bg-background shadow-2xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
          aria-describedby={undefined}
        >
          {ticketKey ? <PanelBody ticketKey={ticketKey} /> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function PanelBody({ ticketKey }: { ticketKey: string }) {
  const { t } = useTranslation()
  const ticket = useTicket(ticketKey)
  const messages = useMessages(ticketKey, 'full')
  const download = useDownloadAttachment(ticketKey)
  const now = useNow(30_000)
  const timeZone = useMemo(() => viewerTimeZone(), [])
  const can = {
    reply: usePermission(PERMISSIONS.messagesReply),
    noteWrite: usePermission(PERMISSIONS.notesWrite),
    attach: usePermission(PERMISSIONS.attachmentsUpload),
    link: usePermission(PERMISSIONS.ticketsLinkRecord),
    assign: usePermission(PERMISSIONS.ticketsAssign),
    update: usePermission(PERMISSIONS.ticketsUpdate),
    resolve: usePermission(PERMISSIONS.ticketsResolve),
    reopen: usePermission(PERMISSIONS.ticketsReopen),
  }
  const [dialog, setDialog] = useState<'resolve' | 'reopen' | null>(null)
  const items = useMemo(
    () => (messages.data && ticket.data ? buildThread(messages.data, ticket.data, 'conversation', timeZone) : []),
    [messages.data, ticket.data, timeZone],
  )

  const header = (
    <header className="flex items-start gap-3 border-b border-line px-6 py-4">
      <div className="flex min-w-0 grow flex-col gap-1.5">
        <span className="font-mono text-meta text-ink-muted">{ticketKey}</span>
        <DialogPrimitive.Title className="text-[20px] font-semibold leading-7 tracking-[-0.01em] text-ink">
          {ticket.data?.subject ?? ticketKey}
        </DialogPrimitive.Title>
        {ticket.data ? (
          <div className="flex flex-wrap items-center gap-2">
            <PriorityMenu ticket={ticket.data} routeId={ticketKey} canUpdate={can.update} />
            <StatusMenu
              ticket={ticket.data}
              routeId={ticketKey}
              can={can}
              onResolve={() => setDialog('resolve')}
              onReopen={() => setDialog('reopen')}
            />
            <span className="mx-1 h-4 w-px bg-line" aria-hidden />
            <AssigneeMenu ticket={ticket.data} routeId={ticketKey} canAssign={can.assign} />
          </div>
        ) : null}
      </div>
      <Link
        to={`/tickets/${ticketKey}`}
        className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-meta text-ink-muted hover:bg-surface-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ExternalLink className="size-4" aria-hidden />
        {t('boards.panel.openFull')}
      </Link>
      <DialogPrimitive.Close
        aria-label={t('boards.panel.close')}
        className="rounded-md p-1.5 text-ink-muted hover:bg-surface-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="size-4" aria-hidden />
      </DialogPrimitive.Close>
    </header>
  )

  if (ticket.isPending) return <>{header}<LoadingState className="px-6" /></>
  if (ticket.isError) return <>{header}<div className="p-6"><ErrorState message={errorMessage(ticket.error)} onRetry={() => void ticket.refetch()} /></div></>

  const done = ticket.data.status === 'resolved' || ticket.data.status === 'closed'
  return (
    <>
      {header}
      <div className="flex min-h-0 grow">
        <section aria-label={t('ticket.tabs.conversation')} className="flex min-w-0 grow flex-col">
          <div className="flex grow flex-col gap-6 overflow-y-auto px-6 py-5">
            {messages.isPending ? (
              <LoadingState />
            ) : messages.isError ? (
              <ErrorState message={errorMessage(messages.error)} onRetry={() => void messages.refetch()} />
            ) : (
              <Thread items={items} timeZone={timeZone} assigneeId={ticket.data.assignee?.id} onDownload={(aid) => download.mutate(aid)} />
            )}
          </div>
          {can.reply || can.noteWrite ? (
            <div className="border-t border-line px-6 py-4">
              <Composer
                routeId={ticketKey}
                author="agent"
                canReply={can.reply && !done}
                canNote={can.noteWrite}
                canAttach={can.attach}
                canLink={can.link}
                replyTo={replyTarget(messages.data ?? [], ticket.data.reporter?.name)}
                showHint={false}
              />
            </div>
          ) : null}
        </section>
        <div className="hidden shrink-0 overflow-y-auto lg:block">
          <TicketRail
            ticket={ticket.data}
            routeId={ticketKey}
            now={now}
            canAssign={can.assign}
            canUpdate={can.update}
            canResolve={can.resolve}
            canReopen={can.reopen}
            isAgent
            onResolve={() => setDialog('resolve')}
            onReopen={() => setDialog('reopen')}
          />
        </div>
      </div>
      {can.resolve ? (
        <ResolveDialog ticket={ticket.data} routeId={ticketKey} open={dialog === 'resolve'} onOpenChange={(o) => setDialog(o ? 'resolve' : null)} />
      ) : null}
      {can.reopen ? (
        <ReopenDialog ticket={ticket.data} routeId={ticketKey} open={dialog === 'reopen'} onOpenChange={(o) => setDialog(o ? 'reopen' : null)} />
      ) : null}
    </>
  )
}
